import { useState, useEffect } from 'react'
import { FiX, FiUpload, FiSave, FiLoader, FiEye } from 'react-icons/fi'
import axios from 'axios'
import toast from 'react-hot-toast'
import CardPreviewModal from './CardPreviewModal'

const TEMPLATES = {
  'minimal-corporate': {
    name: 'Minimal Corporate',
    description: 'Clean, professional design with essential information',
    preview: '🏢'
  },
  'modern-creative': {
    name: 'Modern Creative',
    description: 'Contemporary design with creative elements',
    preview: '🎨'
  },
  'id-style': {
    name: 'ID Style',
    description: 'Employee ID card format with photo and details',
    preview: '🪪'
  }
}

const TEMPLATE_LAYOUTS = {
  'minimal-corporate': {
    backgroundColor: '#ffffff',
    textColor: '#1f2937',
    accentColor: '#3b82f6',
    fontFamily: 'Inter',
    borderRadius: '8px'
  },
  'modern-creative': {
    backgroundColor: '#0f172a',
    textColor: '#f9fafb',
    accentColor: '#ec4899',
    fontFamily: 'Poppins',
    borderRadius: '12px'
  },
  'id-style': {
    backgroundColor: '#f3f4f6',
    textColor: '#111827',
    accentColor: '#10b981',
    fontFamily: 'Inter',
    borderRadius: '10px'
  }
}

const getTemplateLayout = (template) => ({
  ...TEMPLATE_LAYOUTS['minimal-corporate'],
  ...(TEMPLATE_LAYOUTS[template] || {})
})

export default function CardGeneratorModal({ website, employees, card, onGenerateBusiness, onGenerateEmployee, onUpdate, onClose }) {
  const [step, setStep] = useState(card ? 'edit' : 'type')
  const [cardType, setCardType] = useState(card?.cardType || '')
  const [selectedEmployee, setSelectedEmployee] = useState('')
  const normalizeLogo = (logo) => {
    if (!logo) return null
    if (typeof logo === 'string') return { url: logo, publicId: null }
    if (typeof logo === 'object' && logo.url) return logo
    return null
  }

  const [formData, setFormData] = useState(() => {
    const initialTemplate = card?.template || 'minimal-corporate'

    return {
      name: card?.name || '',
      template: initialTemplate,
      cardData: {
        // Business fields
        businessName: card?.cardData?.businessName || '',
        businessLogo: normalizeLogo(card?.cardData?.businessLogo),
        businessEmail: card?.cardData?.businessEmail || '',
        businessPhone: card?.cardData?.businessPhone || '',
        businessAddress: card?.cardData?.businessAddress || '',
        businessWebsite: card?.cardData?.businessWebsite || '',
        businessDescription: card?.cardData?.businessDescription || '',

        // Employee fields
        employeeName: card?.cardData?.employeeName || '',
        employeePosition: card?.cardData?.employeePosition || '',
        employeeEmail: card?.cardData?.employeeEmail || '',
        employeePhone: card?.cardData?.employeePhone || '',
        employeeBio: card?.cardData?.employeeBio || '',
        employeeProfileImage: normalizeLogo(card?.cardData?.employeeProfileImage),

        // Custom fields
        customFields: card?.cardData?.customFields || []
      },
      layout: card?.layout || getTemplateLayout(initialTemplate)
    }
  })
  const [saving, setSaving] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [uploadingFields, setUploadingFields] = useState({})

  // Auto-populate business data when generating
  useEffect(() => {
    if (!card && step === 'business') {
      loadBusinessData()
    }
  }, [step])

  // Auto-populate employee data when selecting employee
  useEffect(() => {
    if (selectedEmployee && employees.length > 0) {
      const employee = employees.find(emp => emp.id === selectedEmployee)
      if (employee) {
        setFormData(prev => ({
          ...prev,
          cardData: {
            ...prev.cardData,
            employeeName: employee.name,
            employeePosition: employee.position,
            employeeBio: employee.bio,
            employeeProfileImage: employee.profileImage
          }
        }))
      }
    }
  }, [selectedEmployee, employees])

  const loadBusinessData = async () => {
    try {
      // This would be handled by the backend, but we can show loading
      setFormData(prev => ({
        ...prev,
        cardData: {
          ...prev.cardData,
          businessName: 'Loading...',
          businessEmail: 'Loading...',
          businessPhone: 'Loading...',
          businessAddress: 'Loading...',
          businessWebsite: 'Loading...'
        }
      }))
    } catch (error) {
      console.error('Failed to load business data:', error)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleCardDataChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      cardData: {
        ...prev.cardData,
        [field]: value
      }
    }))
  }

  const handleLayoutChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      layout: {
        ...prev.layout,
        [field]: value
      }
    }))
  }

  const handleFileUpload = async (file, field) => {
    if (!file) return

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB')
      return
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif']
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPEG, PNG, GIF)')
      return
    }

    setUploadingFields(prev => ({ ...prev, [field]: true }))

    try {
      const formDataUpload = new FormData()
      formDataUpload.append('file', file)
      formDataUpload.append('folder', 'cards')

      const response = await axios.post('/upload', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })

      if (response.data.success) {
        handleCardDataChange(field, {
          url: response.data.url,
          publicId: response.data.publicId
        })
        toast.success('Image uploaded successfully!')
      }
    } catch (error) {
      console.error('Upload failed:', error)
      toast.error('Failed to upload image')
    } finally {
      setUploadingFields(prev => ({ ...prev, [field]: false }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.name || formData.name.trim() === '') {
      toast.error('Card name is required')
      return
    }

    if (cardType === 'employee' && !selectedEmployee && (!formData.cardData.employeeName || formData.cardData.employeeName.trim() === '')) {
      toast.error('Employee name is required when not selecting from team members')
      return
    }

    setSaving(true)

    try {
      if (card) {
        // Update existing card
        await onUpdate(card._id, formData)
      } else {
        // Generate new card
        if (cardType === 'business') {
          await onGenerateBusiness(formData)
        } else if (cardType === 'employee') {
          const payload = {
            ...formData,
            ...(selectedEmployee ? { employeeId: selectedEmployee } : {})
          }
          await onGenerateEmployee(payload)
        }
      }
    } catch (error) {
      console.error('Failed to save card:', error)
    } finally {
      setSaving(false)
    }
  }

  const renderCardTypeSelection = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Choose Card Type</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => {
              setCardType('business')
              setStep('business')
            }}
            className="p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left"
          >
            <div className="text-3xl mb-3">🏢</div>
            <h4 className="font-semibold text-gray-900 mb-2">Business Card</h4>
            <p className="text-sm text-gray-600">Professional business card with company branding</p>
          </button>

          <button
            onClick={() => {
              setCardType('employee')
              setStep('employee')
            }}
            className="p-6 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors text-left"
          >
            <div className="text-3xl mb-3">👤</div>
            <h4 className="font-semibold text-gray-900 mb-2">Employee Card</h4>
            <p className="text-sm text-gray-600">Employee ID card with company branding</p>
          </button>
        </div>
      </div>
    </div>
  )

  const renderTemplateSelection = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Choose Template</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(TEMPLATES).map(([key, template]) => (
            <button
              key={key}
              onClick={() => {
                handleInputChange('template', key)
                setFormData(prev => ({
                  ...prev,
                  template: key,
                  layout: getTemplateLayout(key)
                }))
                setStep('details')
              }}
              className={`p-4 border-2 rounded-lg transition-colors text-left ${
                formData.template === key
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="text-2xl mb-2">{template.preview}</div>
              <h4 className="font-semibold text-gray-900 mb-1">{template.name}</h4>
              <p className="text-xs text-gray-600">{template.description}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  )

  const renderBusinessForm = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Business Card Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Card Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="e.g. Company Business Card"
              className="input-field w-full"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Business Name
            </label>
            <input
              type="text"
              value={formData.cardData.businessName}
              onChange={(e) => handleCardDataChange('businessName', e.target.value)}
              className="input-field w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              type="email"
              value={formData.cardData.businessEmail}
              onChange={(e) => handleCardDataChange('businessEmail', e.target.value)}
              className="input-field w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone
            </label>
            <input
              type="tel"
              value={formData.cardData.businessPhone}
              onChange={(e) => handleCardDataChange('businessPhone', e.target.value)}
              className="input-field w-full"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Address
            </label>
            <textarea
              value={formData.cardData.businessAddress}
              onChange={(e) => handleCardDataChange('businessAddress', e.target.value)}
              rows="2"
              className="input-field w-full resize-none"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Website
            </label>
            <input
              type="url"
              value={formData.cardData.businessWebsite}
              onChange={(e) => handleCardDataChange('businessWebsite', e.target.value)}
              className="input-field w-full"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Business Logo
            </label>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200 rounded cursor-pointer font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed" style={{ opacity: uploadingFields.businessLogo ? 0.6 : 1, pointerEvents: uploadingFields.businessLogo ? 'none' : 'auto' }}>
                {uploadingFields.businessLogo ? (
                  <>
                    <FiLoader className="w-4 h-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <FiUpload className="w-4 h-4" />
                    Upload Logo
                  </>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e.target.files?.[0], 'businessLogo')}
                  disabled={uploadingFields.businessLogo}
                  className="hidden"
                />
              </label>
              {formData.cardData.businessLogo?.url && (
                <img
                  src={formData.cardData.businessLogo.url}
                  alt="Business Logo"
                  className="w-12 h-12 object-cover rounded"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  const renderEmployeeForm = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Employee Card Details</h3>

        {employees.length > 0 && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Employee (optional)
            </label>
            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              className="input-field w-full"
            >
              <option value="">Choose an employee (or enter name manually)</option>
              {employees.map(employee => (
                <option key={employee.id} value={employee.id}>
                  {employee.name} - {employee.position}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Card Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="e.g. John Doe Employee Card"
              className="input-field w-full"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Employee Name
            </label>
            <input
              type="text"
              value={formData.cardData.employeeName}
              onChange={(e) => handleCardDataChange('employeeName', e.target.value)}
              className="input-field w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Position
            </label>
            <input
              type="text"
              value={formData.cardData.employeePosition}
              onChange={(e) => handleCardDataChange('employeePosition', e.target.value)}
              className="input-field w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              type="email"
              value={formData.cardData.employeeEmail}
              onChange={(e) => handleCardDataChange('employeeEmail', e.target.value)}
              className="input-field w-full"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bio
            </label>
            <textarea
              value={formData.cardData.employeeBio}
              onChange={(e) => handleCardDataChange('employeeBio', e.target.value)}
              rows="3"
              className="input-field w-full resize-none"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Profile Image
            </label>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200 rounded cursor-pointer font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed" style={{ opacity: uploadingFields.employeeProfileImage ? 0.6 : 1, pointerEvents: uploadingFields.employeeProfileImage ? 'none' : 'auto' }}>
                {uploadingFields.employeeProfileImage ? (
                  <>
                    <FiLoader className="w-4 h-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <FiUpload className="w-4 h-4" />
                    Upload Photo
                  </>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e.target.files?.[0], 'employeeProfileImage')}
                  disabled={uploadingFields.employeeProfileImage}
                  className="hidden"
                />
              </label>
              {formData.cardData.employeeProfileImage?.url && (
                <img
                  src={formData.cardData.employeeProfileImage.url}
                  alt="Profile"
                  className="w-12 h-12 object-cover rounded-full"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  const renderLayoutSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Layout & Styling</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Background Color
            </label>
            <input
              type="color"
              value={formData.layout.backgroundColor}
              onChange={(e) => handleLayoutChange('backgroundColor', e.target.value)}
              className="w-full h-10 border border-gray-300 rounded"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Text Color
            </label>
            <input
              type="color"
              value={formData.layout.textColor}
              onChange={(e) => handleLayoutChange('textColor', e.target.value)}
              className="w-full h-10 border border-gray-300 rounded"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Accent Color
            </label>
            <input
              type="color"
              value={formData.layout.accentColor}
              onChange={(e) => handleLayoutChange('accentColor', e.target.value)}
              className="w-full h-10 border border-gray-300 rounded"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Border Radius
            </label>
            <select
              value={formData.layout.borderRadius}
              onChange={(e) => handleLayoutChange('borderRadius', e.target.value)}
              className="input-field w-full"
            >
              <option value="0px">Sharp</option>
              <option value="4px">Small</option>
              <option value="8px">Medium</option>
              <option value="12px">Large</option>
              <option value="50%">Round</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  )

  const renderLivePreview = () => {
    const style = {
      backgroundColor: formData.layout.backgroundColor,
      color: formData.layout.textColor,
      border: `2px solid ${formData.layout.accentColor}`,
      borderRadius: formData.layout.borderRadius,
      fontFamily: formData.layout.fontFamily,
      padding: '1rem',
      minHeight: '180px',
      width: '100%',
      maxWidth: '390px',
      boxSizing: 'border-box'
    }

    const textAccent = { color: formData.layout.accentColor }

    if (cardType === 'business') {
      return (
        <div className="mt-6">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Live Preview</h4>
          <div style={style} className="shadow-sm">
            <div className="flex justify-between items-center mb-3">
              <div className="font-bold text-lg" style={textAccent}>{formData.cardData.businessName || 'Business Name'}</div>
              {formData.cardData.businessLogo?.url && (
                <img src={formData.cardData.businessLogo.url} alt="Logo" className="h-8 w-auto object-contain" />
              )}
            </div>
            <div className="text-sm">
              {formData.cardData.businessEmail && <div>Email: {formData.cardData.businessEmail}</div>}
              {formData.cardData.businessPhone && <div>Phone: {formData.cardData.businessPhone}</div>}
              {formData.cardData.businessAddress && <div>Address: {formData.cardData.businessAddress}</div>}
              {formData.cardData.businessWebsite && <div>Website: {formData.cardData.businessWebsite}</div>}
            </div>
            <p className="text-xs mt-3 opacity-70">{formData.cardData.businessDescription || 'Business description'}</p>
          </div>
        </div>
      )
    }

    return (
      <div className="mt-6">
        <h4 className="text-sm font-semibold text-gray-900 mb-2">Live Preview</h4>
        <div style={style} className="shadow-sm flex items-center gap-3">
          <div>
            {formData.cardData.employeeProfileImage?.url ? (
              <img src={formData.cardData.employeeProfileImage.url} alt="Profile" className="w-16 h-16 rounded-full object-cover" style={{ border: `2px solid ${formData.layout.accentColor}` }} />
            ) : (
              <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: formData.layout.accentColor + '22', color: formData.layout.accentColor }}>
                👤
              </div>
            )}
          </div>
          <div>
            <div className="text-lg font-bold" style={textAccent}>{formData.cardData.employeeName || 'Employee Name'}</div>
            <div className="text-sm font-medium">{formData.cardData.employeePosition || 'Position'}</div>
            <div className="text-xs mt-1">
              {formData.cardData.employeeEmail && <div>{formData.cardData.employeeEmail}</div>}
              {formData.cardData.employeePhone && <div>{formData.cardData.employeePhone}</div>}
              {formData.cardData.businessName && <div>{formData.cardData.businessName}</div>}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {card ? 'Edit Card' : 'Generate New Card'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {step === 'type' && renderCardTypeSelection()}
          {step === 'business' && renderTemplateSelection()}
          {step === 'employee' && renderTemplateSelection()}
          {step === 'details' && (
            <>
              {cardType === 'business' ? renderBusinessForm() : renderEmployeeForm()}
              {renderLayoutSettings()}
              {renderLivePreview()}
            </>
          )}
          {step === 'edit' && (
            <>
              {card.cardType === 'business' ? renderBusinessForm() : renderEmployeeForm()}
              {renderLayoutSettings()}
              {renderLivePreview()}
            </>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => setShowPreview(true)}
              className="btn-secondary flex items-center gap-2"
            >
              <FiEye className="w-4 h-4" />
              Preview Card
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary flex items-center gap-2"
            >
              {saving ? (
                <>
                  <FiLoader className="w-4 h-4 animate-spin" />
                  {card ? 'Updating...' : 'Generating...'}
                </>
              ) : (
                <>
                  <FiSave className="w-4 h-4" />
                  {card ? 'Update Card' : 'Generate Card'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <CardPreviewModal
          card={card || {}}
          previewData={{
            ...formData,
            cardType,
            createdAt: card?.createdAt || new Date(),
            isPublic: card?.isPublic || false
          }}
          onClose={() => setShowPreview(false)}
          onExport={null} // Disable export from preview
        />
      )}
    </div>
  )
}